import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createAdminBlogCategory,
  createAdminBlogPost,
  deleteAdminBlogPost,
  getAdminBlogCategories,
  getAdminBlogPosts,
  updateAdminBlogPost,
} from "../api/admin";
import { getUser } from "../auth/auth";
import { useToast } from "../components/ToastProvider";
import type { BlogCategory, BlogPost, BlogPostPayload } from "../types/blogs";
import "../styles/AdminBlogs.css";

type ImageKey = "cover" | "primary" | "main";
type BlockTag = "P" | "H2" | "H3" | "BLOCKQUOTE";
type ToolbarState = {
  block: BlockTag;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  unorderedList: boolean;
  orderedList: boolean;
  link: boolean;
};
type FormState = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readingTime: string;
  author: string;
  authorRole: string;
  spotlight: string;
  accent: string;
  coverImageSrc: string;
  coverImageAlt: string;
  coverImageWidth: string;
  coverImageHeight: string;
  removeCoverImage: boolean;
  primaryImageSrc: string;
  primaryImageAlt: string;
  primaryImageWidth: string;
  primaryImageHeight: string;
  removePrimaryImage: boolean;
  mainImageSrc: string;
  mainImageAlt: string;
  mainImageWidth: string;
  mainImageHeight: string;
  removeMainImage: boolean;
  bodyHtml: string;
  isPublished: boolean;
};

type ValidationErrors = Partial<Record<"slug" | "title" | "excerpt" | "category" | "readingTime" | "author" | "authorRole" | "bodyHtml", string>>;

const IMAGE_DEFAULT_WIDTH = "1200";
const IMAGE_DEFAULT_HEIGHT = "750";
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const BLOCK_TAG_VALUES: Record<BlockTag, string> = {
  P: "<p>",
  H2: "<h2>",
  H3: "<h3>",
  BLOCKQUOTE: "<blockquote>",
};
const DEFAULT_TOOLBAR_STATE: ToolbarState = {
  block: "P",
  bold: false,
  italic: false,
  underline: false,
  unorderedList: false,
  orderedList: false,
  link: false,
};

const defaultFormState = (): FormState => ({
  slug: "",
  title: "",
  excerpt: "",
  category: "",
  readingTime: "",
  author: "",
  authorRole: "",
  spotlight: "",
  accent: "",
  coverImageSrc: "",
  coverImageAlt: "",
  coverImageWidth: IMAGE_DEFAULT_WIDTH,
  coverImageHeight: IMAGE_DEFAULT_HEIGHT,
  removeCoverImage: false,
  primaryImageSrc: "",
  primaryImageAlt: "",
  primaryImageWidth: IMAGE_DEFAULT_WIDTH,
  primaryImageHeight: IMAGE_DEFAULT_HEIGHT,
  removePrimaryImage: false,
  mainImageSrc: "",
  mainImageAlt: "",
  mainImageWidth: IMAGE_DEFAULT_WIDTH,
  mainImageHeight: IMAGE_DEFAULT_HEIGHT,
  removeMainImage: false,
  bodyHtml: "",
  isPublished: true,
});

function formFromPost(post: BlogPost): FormState {
  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    category: post.categoryId,
    readingTime: post.readingTime,
    author: post.author,
    authorRole: post.authorRole,
    spotlight: post.spotlight ?? "",
    accent: post.accent ?? "",
    coverImageSrc: post.coverImage?.src ?? "",
    coverImageAlt: post.coverImage?.alt ?? "",
    coverImageWidth: String(post.coverImage?.width ?? IMAGE_DEFAULT_WIDTH),
    coverImageHeight: String(post.coverImage?.height ?? IMAGE_DEFAULT_HEIGHT),
    removeCoverImage: false,
    primaryImageSrc: post.primaryImage?.src ?? "",
    primaryImageAlt: post.primaryImage?.alt ?? "",
    primaryImageWidth: String(post.primaryImage?.width ?? IMAGE_DEFAULT_WIDTH),
    primaryImageHeight: String(post.primaryImage?.height ?? IMAGE_DEFAULT_HEIGHT),
    removePrimaryImage: false,
    mainImageSrc: post.mainImage?.src ?? "",
    mainImageAlt: post.mainImage?.alt ?? "",
    mainImageWidth: String(post.mainImage?.width ?? IMAGE_DEFAULT_WIDTH),
    mainImageHeight: String(post.mainImage?.height ?? IMAGE_DEFAULT_HEIGHT),
    removeMainImage: false,
    bodyHtml: post.bodyHtml,
    isPublished: Boolean(post.isPublished),
  };
}

function toImagePayload(src: string, alt: string, width: string, height: string) {
  const trimmedSrc = src.trim();
  if (!trimmedSrc) {
    return undefined;
  }

  return {
    src: trimmedSrc,
    alt: alt.trim(),
    width: Number(width) || 1200,
    height: Number(height) || 750,
  };
}

function ToolbarIcon({ children }: { children?: any }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function getToolbarButtonClassName(isActive = false) {
  return `admin-blogs-tool-button${isActive ? " is-active" : ""}`;
}

function getImageFieldNames(prefix: ImageKey) {
  if (prefix === "cover") {
    return {
      src: "coverImageSrc" as const,
      alt: "coverImageAlt" as const,
      width: "coverImageWidth" as const,
      height: "coverImageHeight" as const,
      remove: "removeCoverImage" as const,
    };
  }

  if (prefix === "primary") {
    return {
      src: "primaryImageSrc" as const,
      alt: "primaryImageAlt" as const,
      width: "primaryImageWidth" as const,
      height: "primaryImageHeight" as const,
      remove: "removePrimaryImage" as const,
    };
  }

  return {
    src: "mainImageSrc" as const,
    alt: "mainImageAlt" as const,
    width: "mainImageWidth" as const,
    height: "mainImageHeight" as const,
    remove: "removeMainImage" as const,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === "object") {
    const candidate = error as { message?: unknown; data?: { message?: unknown } };
    if (typeof candidate.message === "string" && candidate.message.trim()) {
      return candidate.message;
    }
    if (typeof candidate.data?.message === "string" && candidate.data.message.trim()) {
      return candidate.data.message;
    }
  }

  return "Something went wrong";
}

function createSlugFromTitle(title: string) {
  return title
    .toLowerCase()
    .trim()
    .replace(/['"`]+/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function validateForm(state: FormState, bodyHtml: string): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!state.slug.trim()) {
    errors.slug = "Slug is required.";
  } else if (!SLUG_PATTERN.test(state.slug.trim())) {
    errors.slug = "Slug must use lowercase letters, numbers, and hyphens only.";
  }

  if (!state.title.trim()) {
    errors.title = "Title is required.";
  }

  if (!state.excerpt.trim()) {
    errors.excerpt = "Excerpt is required.";
  }

  if (!state.category.trim()) {
    errors.category = "Category is required.";
  }

  if (!state.readingTime.trim()) {
    errors.readingTime = "Reading time is required.";
  }

  if (!state.author.trim()) {
    errors.author = "Author is required.";
  }

  if (!state.authorRole.trim()) {
    errors.authorRole = "Author role is required.";
  }

  if (!bodyHtml.trim()) {
    errors.bodyHtml = "Article body is required.";
  }

  return errors;
}

export default function AdminBlogs() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  const editorRef = useRef(null as HTMLDivElement | null);
  const savedSelectionRef = useRef(null as Range | null);
  const [posts, setPosts] = useState([] as BlogPost[]);
  const [categories, setCategories] = useState([] as BlogCategory[]);
  const [formState, setFormState] = useState(defaultFormState());
  const [status, setStatus] = useState("Loading blog posts...");
  const [loading, setLoading] = useState(true);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null as string | null);
  const [validationErrors, setValidationErrors] = useState({} as ValidationErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [toolbarState, setToolbarState] = useState(DEFAULT_TOOLBAR_STATE);

  const loadPosts = useCallback(async () => {
    try {
      setStatus("Loading blog posts...");
      const [blogPosts, blogCategories] = await Promise.all([getAdminBlogPosts(), getAdminBlogCategories()]);
      setPosts(blogPosts);
      setCategories(blogCategories);
      setStatus(`${blogPosts.length} blog post${blogPosts.length === 1 ? "" : "s"} loaded`);
    } catch (error) {
      const message = getErrorMessage(error);
      setStatus(message);

      if (message.toLowerCase().includes("forbidden") || message.toLowerCase().includes("unauthorized")) {
        showToast("Unauthorized access", "error");
        navigate("/home");
        return;
      }

      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      showToast("Unauthorized access", "error");
      navigate("/home");
      return;
    }

    void loadPosts();
  }, [loadPosts, navigate, showToast, user]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== formState.bodyHtml) {
      editorRef.current.innerHTML = formState.bodyHtml;
    }
  }, [formState.bodyHtml]);

  useEffect(() => {
    function handleSelectionChange() {
      updateToolbarState();
    }

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  });

  function isNodeInsideEditor(node: Node | null) {
    if (!node || !editorRef.current) {
      return false;
    }

    const elementNode = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return Boolean(elementNode && editorRef.current.contains(elementNode));
  }

  function saveSelection() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!isNodeInsideEditor(range.commonAncestorContainer)) {
      return;
    }

    savedSelectionRef.current = range.cloneRange();
  }

  function restoreSelection(fallbackToEnd = true) {
    if (!editorRef.current) {
      return false;
    }

    const selection = window.getSelection();
    if (!selection) {
      return false;
    }

    const savedRange = savedSelectionRef.current;
    if (savedRange && isNodeInsideEditor(savedRange.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(savedRange.cloneRange());
      return true;
    }

    if (!fallbackToEnd) {
      return false;
    }

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    savedSelectionRef.current = range.cloneRange();
    return true;
  }

  function getSelectionElement() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) {
      return null;
    }

    const anchorNode = selection.anchorNode;
    const elementNode = anchorNode?.nodeType === Node.ELEMENT_NODE ? anchorNode : anchorNode?.parentElement ?? null;
    if (!elementNode || !editorRef.current.contains(elementNode)) {
      return null;
    }

    return elementNode as HTMLElement;
  }

  function updateToolbarState() {
    const element = getSelectionElement();
    if (!element) {
      setToolbarState(DEFAULT_TOOLBAR_STATE);
      return;
    }

    const nextBlock = (["BLOCKQUOTE", "H3", "H2"] as BlockTag[]).find((tag) => Boolean(element.closest(tag.toLowerCase()))) ?? "P";

    setToolbarState({
      block: nextBlock,
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      unorderedList: document.queryCommandState("insertUnorderedList"),
      orderedList: document.queryCommandState("insertOrderedList"),
      link: Boolean(element.closest("a")),
    });
  }

  function updateField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setFormState((current: FormState) => ({ ...current, [key]: value }));
  }

  function generateSlugFromTitle() {
    updateField("slug", createSlugFromTitle(formState.title));
    setValidationErrors((current: ValidationErrors) => ({ ...current, slug: undefined }));
  }

  function resetForm() {
    setFormState(defaultFormState());
    setValidationErrors({});
    clearSelectedImageState();
  }

  function syncEditorHtml() {
    const html = editorRef.current?.innerHTML ?? "";
    updateField("bodyHtml", html);
    setValidationErrors((current: ValidationErrors) => ({ ...current, bodyHtml: undefined }));
    updateToolbarState();
  }

  function applyFormat(command: string, value?: string) {
    if (!editorRef.current) {
      return;
    }

    editorRef.current.focus();
    restoreSelection();
    document.execCommand(command, false, value);
    saveSelection();
    syncEditorHtml();
  }

  function applyBlock(tag: BlockTag) {
    if (!editorRef.current) {
      return;
    }

    editorRef.current.focus();
    restoreSelection();
    document.execCommand("formatBlock", false, BLOCK_TAG_VALUES[tag]);
    saveSelection();
    syncEditorHtml();
  }

  function insertImageIntoEditor(src: string, alt: string) {
    if (!src.trim() || !editorRef.current) {
      return;
    }

    editorRef.current.focus();
    restoreSelection();
    document.execCommand(
      "insertHTML",
      false,
      `<figure><img src="${src}" alt="${alt || "Blog image"}" /><figcaption>${alt || "Image caption"}</figcaption></figure>`,
    );
    saveSelection();
    syncEditorHtml();
  }

  function promptAndInsertImage() {
    saveSelection();
    const src = window.prompt("Enter image URL");
    if (!src) {
      return;
    }

    const alt = window.prompt("Enter image alt text") ?? "Blog image";
    insertImageIntoEditor(src, alt);
  }

  function createLink() {
    saveSelection();
    const url = window.prompt("Enter link URL");
    if (!url) {
      return;
    }

    const normalizedUrl =
      /^(https?:|mailto:|tel:|\/|#)/i.test(url.trim()) || !url.trim()
        ? url.trim()
        : `https://${url.trim()}`;

    applyFormat("createLink", normalizedUrl);
  }

  function clearSelectedImageState() {
    editorRef.current
      ?.querySelectorAll("img[data-editor-selected='true']")
      .forEach((image: Element) => image.removeAttribute("data-editor-selected"));
    setSelectedImageSrc(null);
  }

  function removeSelectedImage() {
    if (!editorRef.current) {
      return;
    }

    const selectedImage = editorRef.current.querySelector("img[data-editor-selected='true']") as HTMLImageElement | null;
    if (!selectedImage) {
      return;
    }

    const figure = selectedImage.closest("figure");
    if (figure) {
      figure.remove();
    } else {
      selectedImage.remove();
    }

    clearSelectedImageState();
    syncEditorHtml();
    updateToolbarState();
  }

  function handleEditorClick(event: { target: EventTarget | null }) {
    const target = event.target;

    if (!(target instanceof HTMLElement) || !editorRef.current) {
      return;
    }

    clearSelectedImageState();

    if (target instanceof HTMLImageElement) {
      target.setAttribute("data-editor-selected", "true");
      setSelectedImageSrc(target.getAttribute("src"));
    }

    saveSelection();
    updateToolbarState();
  }

  function handleToolbarMouseDown(event: any) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest("button")) {
      return;
    }

    saveSelection();
    event.preventDefault();
  }

  async function handleImageUpload(event: any, prefix: ImageKey) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const fieldNames = getImageFieldNames(prefix);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ""));
      reader.onerror = () => reject(new Error("Image upload failed"));
      reader.readAsDataURL(file);
    });

    const image = new window.Image();
    image.onload = () => {
      updateField(fieldNames.src, dataUrl);
      updateField(fieldNames.alt, file.name);
      updateField(fieldNames.width, String(image.width || 1200));
      updateField(fieldNames.height, String(image.height || 750));
      updateField(fieldNames.remove, false);
    };
    image.src = dataUrl;
    event.target.value = "";
  }

  function handleRemoveTopLevelImage(prefix: ImageKey) {
    const fieldNames = getImageFieldNames(prefix);
    updateField(fieldNames.src, "");
    updateField(fieldNames.alt, "");
    updateField(fieldNames.width, IMAGE_DEFAULT_WIDTH);
    updateField(fieldNames.height, IMAGE_DEFAULT_HEIGHT);
    updateField(fieldNames.remove, true);
  }

  async function handleSubmit(event: any) {
    event.preventDefault();

    const bodyHtml = editorRef.current?.innerHTML ?? formState.bodyHtml;
    const errors = validateForm(formState, bodyHtml);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setStatus("Fix the validation errors before saving.");
      showToast("Please fix the blog form errors", "error");
      return;
    }

    const payload: BlogPostPayload = {
      slug: formState.slug.trim(),
      title: formState.title.trim(),
      excerpt: formState.excerpt.trim(),
      categoryId: formState.category,
      readingTime: formState.readingTime.trim(),
      author: formState.author.trim(),
      authorRole: formState.authorRole.trim(),
      spotlight: formState.spotlight.trim(),
      accent: formState.accent.trim(),
      coverImage: toImagePayload(
        formState.coverImageSrc,
        formState.coverImageAlt,
        formState.coverImageWidth,
        formState.coverImageHeight,
      ),
      removeCoverImage: formState.removeCoverImage,
      primaryImage: toImagePayload(
        formState.primaryImageSrc,
        formState.primaryImageAlt,
        formState.primaryImageWidth,
        formState.primaryImageHeight,
      ),
      removePrimaryImage: formState.removePrimaryImage,
      mainImage: toImagePayload(
        formState.mainImageSrc,
        formState.mainImageAlt,
        formState.mainImageWidth,
        formState.mainImageHeight,
      ),
      removeMainImage: formState.removeMainImage,
      bodyHtml,
      isPublished: formState.isPublished,
    };

    try {
      setIsSubmitting(true);
      setStatus(formState.id ? "Updating post..." : "Creating post...");

      if (formState.id) {
        await updateAdminBlogPost(formState.id, payload);
        showToast("Blog post updated", "success");
      } else {
        await createAdminBlogPost(payload);
        showToast("Blog post created", "success");
      }

      resetForm();
      await loadPosts();
    } catch (error) {
      const message = getErrorMessage(error);
      setStatus(message);
      showToast(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(postId?: string) {
    if (!postId) {
      return;
    }

    const confirmed = window.confirm("Delete this blog post?");
    if (!confirmed) {
      return;
    }

    try {
      setStatus("Deleting post...");
      await deleteAdminBlogPost(postId);

      if (formState.id === postId) {
        resetForm();
      }

      showToast("Blog post deleted", "success");
      await loadPosts();
    } catch (error) {
      const message = getErrorMessage(error);
      setStatus(message);
      showToast(message, "error");
    }
  }

  async function handleCreateCategory() {
    const name = newCategoryName.trim();
    if (!name) {
      showToast("Enter a category name first", "error");
      return;
    }

    try {
      setIsCreatingCategory(true);
      const created = await createAdminBlogCategory({ name });
      setCategories((current: BlogCategory[]) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      updateField("category", created.id);
      setNewCategoryName("");
      setValidationErrors((current: ValidationErrors) => ({ ...current, category: undefined }));
      showToast("Blog category created", "success");
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setIsCreatingCategory(false);
    }
  }

  if (loading) {
    return (
      <div className="admin-blogs-page">
        <div className="loading">Loading blog posts...</div>
      </div>
    );
  }

  return (
    <div className="admin-blogs-page">
      <header className="admin-blogs-header">
        <button className="back-btn" onClick={() => navigate("/home")}>←</button>
        <div>
          <h1>Blog Management</h1>
          <p>Publish and edit the blog content consumed by the Cardex landing page.</p>
        </div>
      </header>

      <section className="admin-blogs-hero">
        <div>
          <span className="admin-blogs-eyebrow">Editorial</span>
          <h2>Authoring now lives in the TCG admin app</h2>
          <p>The public Cardex landing page reads published blogs from the backend at runtime.</p>
        </div>
        <div className="admin-blogs-status">{status}</div>
      </section>

      <div className="admin-blogs-grid">
        <section className="admin-blogs-panel">
          <div className="admin-blogs-panel-header">
            <h3>{formState.id ? "Edit blog post" : "New blog post"}</h3>
            <button type="button" className="refresh-btn" onClick={resetForm}>
              Reset
            </button>
          </div>

          <form className="admin-blogs-form" onSubmit={handleSubmit}>
            <div className="admin-blogs-inline-fields">
              <label>
                <span>Slug</span>
                <div className="admin-blogs-slug-row">
                  <input value={formState.slug} onChange={(event: any) => updateField("slug", event.target.value)} />
                  <button
                    type="button"
                    className="admin-blogs-slug-button"
                    onClick={generateSlugFromTitle}
                    disabled={!formState.title.trim()}
                  >
                    Generate
                  </button>
                </div>
                {validationErrors.slug ? <small className="admin-blogs-error">{validationErrors.slug}</small> : null}
              </label>
              <label>
                <span>Category</span>
                <select value={formState.category} onChange={(event: any) => updateField("category", event.target.value)}>
                  <option value="">Select a category</option>
                  {categories.map((category: BlogCategory) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {validationErrors.category ? <small className="admin-blogs-error">{validationErrors.category}</small> : null}
              </label>
            </div>

            <div className="admin-blogs-category-create">
              <label>
                <span>Create category</span>
                <input value={newCategoryName} onChange={(event: any) => setNewCategoryName(event.target.value)} placeholder="Add a new category" />
              </label>
              <button type="button" className="refresh-btn" onClick={() => void handleCreateCategory()} disabled={isCreatingCategory}>
                {isCreatingCategory ? "Creating..." : "Add Category"}
              </button>
            </div>

            <label>
              <span>Title</span>
              <input value={formState.title} onChange={(event: any) => updateField("title", event.target.value)} />
              {validationErrors.title ? <small className="admin-blogs-error">{validationErrors.title}</small> : null}
            </label>

            <label>
              <span>Excerpt</span>
              <textarea rows={3} value={formState.excerpt} onChange={(event: any) => updateField("excerpt", event.target.value)} />
              {validationErrors.excerpt ? <small className="admin-blogs-error">{validationErrors.excerpt}</small> : null}
            </label>

            <div className="admin-blogs-inline-fields">
              <label>
                <span>Reading time</span>
                <input value={formState.readingTime} onChange={(event: any) => updateField("readingTime", event.target.value)} />
                {validationErrors.readingTime ? <small className="admin-blogs-error">{validationErrors.readingTime}</small> : null}
              </label>
              <label>
                <span>Accent gradient</span>
                <input value={formState.accent} onChange={(event: any) => updateField("accent", event.target.value)} />
              </label>
            </div>

            <div className="admin-blogs-inline-fields">
              <label>
                <span>Author</span>
                <input value={formState.author} onChange={(event: any) => updateField("author", event.target.value)} />
                {validationErrors.author ? <small className="admin-blogs-error">{validationErrors.author}</small> : null}
              </label>
              <label>
                <span>Author role</span>
                <input value={formState.authorRole} onChange={(event: any) => updateField("authorRole", event.target.value)} />
                {validationErrors.authorRole ? <small className="admin-blogs-error">{validationErrors.authorRole}</small> : null}
              </label>
            </div>

            <label>
              <span>Spotlight</span>
              <input value={formState.spotlight} onChange={(event: any) => updateField("spotlight", event.target.value)} />
            </label>

            <div className="admin-blogs-upload-grid">
              {([
                {
                  key: "cover",
                  title: "Cover Photo",
                  hint: "Hero image used at the top of the article and in blog listings.",
                  src: formState.coverImageSrc,
                  alt: formState.coverImageAlt,
                  width: formState.coverImageWidth,
                  height: formState.coverImageHeight,
                },
                {
                  key: "primary",
                  title: "Primary Image",
                  hint: "Supporting visual you can also insert into the editor body.",
                  src: formState.primaryImageSrc,
                  alt: formState.primaryImageAlt,
                  width: formState.primaryImageWidth,
                  height: formState.primaryImageHeight,
                },
                {
                  key: "main",
                  title: "Main Image",
                  hint: "Secondary article visual for richer layouts and inline storytelling.",
                  src: formState.mainImageSrc,
                  alt: formState.mainImageAlt,
                  width: formState.mainImageWidth,
                  height: formState.mainImageHeight,
                },
              ] as Array<{ key: ImageKey; title: string; hint: string; src: string; alt: string; width: string; height: string }>).map((item) => (
                <div key={item.key} className="admin-blogs-upload-card">
                  <div className="admin-blogs-upload-header">
                    <div className="admin-blogs-upload-copy">
                      <strong>{item.title}</strong>
                      <p>{item.hint}</p>
                    </div>
                    <div className="admin-blogs-upload-actions">
                      <label className="admin-blogs-upload-trigger">
                        <span className="admin-blogs-upload-trigger-icon" aria-hidden="true">+</span>
                        <span>{item.src ? "Replace" : "Upload"}</span>
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(event: any) => void handleImageUpload(event, item.key)}
                        />
                      </label>
                      <button
                        type="button"
                        className="admin-blogs-upload-remove"
                        onClick={() => handleRemoveTopLevelImage(item.key)}
                        disabled={!item.src}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="admin-blogs-preview-shell">
                    {item.src ? (
                      <>
                        <img src={item.src} alt={item.alt || item.title} className="admin-blogs-preview-image" />
                        <div className="admin-blogs-preview-badge">Ready</div>
                      </>
                    ) : (
                      <div className="admin-blogs-preview-placeholder">
                        <div className="admin-blogs-preview-placeholder-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="5" width="18" height="14" rx="3" />
                            <path d="M8 13l2.5-2.5 2.5 2.5 3.5-4 3.5 4.5" />
                            <circle cx="9" cy="9" r="1.25" />
                          </svg>
                        </div>
                        <strong>Upload an image</strong>
                        <p>PNG, JPG, or WEBP. A preview will appear here instantly.</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="admin-blogs-editor-panel">
              <div className="admin-blogs-toolbar" onMouseDown={handleToolbarMouseDown}>
                <button type="button" className={getToolbarButtonClassName(toolbarState.block === "P")} onClick={() => applyBlock("P")} title="Paragraph">
                  <ToolbarIcon>
                    <path d="M5 6h14" />
                    <path d="M5 12h10" />
                    <path d="M5 18h8" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.block === "H2")} onClick={() => applyBlock("H2")} title="Heading 2">
                  <ToolbarIcon>
                    <path d="M4 7v10" />
                    <path d="M10 7v10" />
                    <path d="M4 12h6" />
                    <path d="M15 9c0-1.1.9-2 2-2h1c1.1 0 2 .9 2 2 0 .7-.3 1.3-.8 1.7L15 17h5" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.block === "H3")} onClick={() => applyBlock("H3")} title="Heading 3">
                  <ToolbarIcon>
                    <path d="M4 7v10" />
                    <path d="M10 7v10" />
                    <path d="M4 12h6" />
                    <path d="M16 8h2.5a2 2 0 0 1 0 4H17" />
                    <path d="M17 12h1.5a2 2 0 0 1 0 4H16" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.bold)} onClick={() => applyFormat("bold")} title="Bold">
                  <ToolbarIcon>
                    <path d="M8 6h6a3 3 0 0 1 0 6H8z" />
                    <path d="M8 12h7a3 3 0 0 1 0 6H8z" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.italic)} onClick={() => applyFormat("italic")} title="Italic">
                  <ToolbarIcon>
                    <path d="M13 5h6" />
                    <path d="M5 19h6" />
                    <path d="M14 5L10 19" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.underline)} onClick={() => applyFormat("underline")} title="Underline">
                  <ToolbarIcon>
                    <path d="M7 5v6a5 5 0 0 0 10 0V5" />
                    <path d="M5 19h14" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.unorderedList)} onClick={() => applyFormat("insertUnorderedList")} title="Bullet list">
                  <ToolbarIcon>
                    <circle cx="6" cy="7" r="1" />
                    <circle cx="6" cy="12" r="1" />
                    <circle cx="6" cy="17" r="1" />
                    <path d="M10 7h9" />
                    <path d="M10 12h9" />
                    <path d="M10 17h9" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.orderedList)} onClick={() => applyFormat("insertOrderedList")} title="Numbered list">
                  <ToolbarIcon>
                    <path d="M4 7h1v4" />
                    <path d="M4 11h2" />
                    <path d="M4 17h2l-2 2h2" />
                    <path d="M10 7h9" />
                    <path d="M10 12h9" />
                    <path d="M10 17h9" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.block === "BLOCKQUOTE")} onClick={() => applyBlock("BLOCKQUOTE")} title="Quote">
                  <ToolbarIcon>
                    <path d="M8 8H5v5h4v3H6" />
                    <path d="M17 8h-3v5h4v3h-3" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(toolbarState.link)} onClick={createLink} title="Insert link">
                  <ToolbarIcon>
                    <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1" />
                    <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(false)} onClick={() => applyFormat("unlink")} title="Remove link" disabled={!toolbarState.link}>
                  <ToolbarIcon>
                    <path d="M9 15a5 5 0 0 1 0-7l1-1a5 5 0 0 1 6.5-.5" />
                    <path d="M15 9a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-6.5.5" />
                    <path d="M4 20L20 4" />
                  </ToolbarIcon>
                </button>
                <button
                  type="button"
                  className={getToolbarButtonClassName(false)}
                  onClick={() => insertImageIntoEditor(formState.coverImageSrc, formState.coverImageAlt)}
                  title={formState.coverImageSrc ? "Insert cover image" : "Upload a cover image first"}
                  disabled={!formState.coverImageSrc}
                >
                  <ToolbarIcon>
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <circle cx="9" cy="10" r="1.5" />
                    <path d="M20 15l-4-4-7 7" />
                  </ToolbarIcon>
                </button>
                <button
                  type="button"
                  className={getToolbarButtonClassName(false)}
                  onClick={() => insertImageIntoEditor(formState.primaryImageSrc, formState.primaryImageAlt)}
                  title={formState.primaryImageSrc ? "Insert primary image" : "Upload a primary image first"}
                  disabled={!formState.primaryImageSrc}
                >
                  <ToolbarIcon>
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M8 15l3-3 2 2 3-4 4 5" />
                  </ToolbarIcon>
                </button>
                <button
                  type="button"
                  className={getToolbarButtonClassName(false)}
                  onClick={() => insertImageIntoEditor(formState.mainImageSrc, formState.mainImageAlt)}
                  title={formState.mainImageSrc ? "Insert main image" : "Upload a main image first"}
                  disabled={!formState.mainImageSrc}
                >
                  <ToolbarIcon>
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M7 15l4-4 2 2 4-5" />
                    <path d="M12 8v8" />
                  </ToolbarIcon>
                </button>
                <button type="button" className={getToolbarButtonClassName(false)} onClick={promptAndInsertImage} title="Insert image from URL">
                  <ToolbarIcon>
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M12 8v8" />
                    <path d="M8 12h8" />
                  </ToolbarIcon>
                </button>
                <button
                  type="button"
                  className={getToolbarButtonClassName(Boolean(selectedImageSrc))}
                  onClick={removeSelectedImage}
                  title={selectedImageSrc ? "Remove selected image" : "Select an image in the editor first"}
                  disabled={!selectedImageSrc}
                >
                  <ToolbarIcon>
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M8 8l8 8" />
                    <path d="M16 8l-8 8" />
                  </ToolbarIcon>
                </button>
              </div>

              <div className="admin-blogs-editor-hint">
                {selectedImageSrc
                  ? "Image selected. Use the remove-image tool to delete it from the article."
                  : "Tip: click an image inside the editor to select it for removal."}
              </div>

              <div
                ref={editorRef}
                className="admin-blogs-editor"
                contentEditable
                suppressContentEditableWarning
                onFocus={saveSelection}
                onInput={() => {
                  saveSelection();
                  syncEditorHtml();
                }}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onClick={handleEditorClick}
              />
              {validationErrors.bodyHtml ? <small className="admin-blogs-error">{validationErrors.bodyHtml}</small> : null}
            </div>

            <label className="admin-blogs-checkbox">
              <input
                type="checkbox"
                checked={formState.isPublished}
                onChange={(event: any) => updateField("isPublished", event.target.checked)}
              />
              <span>Published</span>
            </label>

            <button type="submit" className="admin-blogs-submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : formState.id ? "Update Post" : "Create Post"}
            </button>
          </form>
        </section>

        <aside className="admin-blogs-panel admin-blogs-list-panel">
          <div className="admin-blogs-panel-header">
            <h3>Existing posts</h3>
            <button type="button" className="refresh-btn" onClick={() => void loadPosts()}>
              Refresh
            </button>
          </div>

          <div className="admin-blogs-list">
            {posts.length === 0 ? (
              <div className="admin-blogs-empty">No blog posts found.</div>
            ) : (
              posts.map((post: BlogPost) => (
                <article key={post.id} className="admin-blogs-list-item">
                  <div className="admin-blogs-list-copy">
                    <span className={`action-pill ${post.isPublished ? "action-approve" : "action-review"}`}>
                      {post.isPublished ? "Published" : "Draft"}
                    </span>
                    <h4>{post.title}</h4>
                    <p>{post.excerpt}</p>
                    <div className="admin-blogs-meta">
                      <span>{post.slug}</span>
                      <span>{post.category}</span>
                      <span>{post.updatedAt ? new Date(post.updatedAt).toLocaleString() : "Unknown"}</span>
                    </div>
                  </div>
                  <div className="admin-blogs-list-actions">
                    <button
                      type="button"
                      className="refresh-btn"
                      onClick={() => {
                        setFormState(formFromPost(post));
                        setValidationErrors({});
                        clearSelectedImageState();
                      }}
                    >
                      Edit
                    </button>
                    <button type="button" className="reject-btn" onClick={() => void handleDelete(post.id)}>
                      Delete
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
